import { Inject, Injectable, NgZone, DOCUMENT } from '@angular/core';

import { ConfigService } from 'src/app/services/json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { LoggerService } from './logger/logger.service';
import { InAppBrowserService } from './in-app-browser.service';
import { Router } from '@angular/router';
import { ToastNotificationService } from './toast-notification/toast-notification.service';
import { ToastNotificationComponent } from '../shared/toast-notification/toast-notification.component';
import { Subscription } from 'rxjs';
import { ApiDevicePlatforms } from './rest-api/rest-api';
import { Browser } from '@capacitor/browser';
import { NativeCheckService } from './native-check.service';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private browserClose$: null | Subscription = null;
  private urlChange$: null | Subscription = null;

  private _localAppUrl = '';

  public get localAppUrl(): string {
    const origin = window?.location?.origin;
    if(origin) {
      this._localAppUrl = origin;
    }

    return this._localAppUrl;
  }

  public constructor(
    @Inject(DOCUMENT) private browserDocument: Document,
    private configService: ConfigService,
    private loggerService: LoggerService,
    private browserService: InAppBrowserService,
    private zone: NgZone,
    private router: Router,
    private toastService: ToastNotificationService,
    private nativeCheckService: NativeCheckService,
  ) { }

  public goToInvestmentQuestionaireExternalForm(): void {
    this.goToExternalUrl(
      EnvironmentKeys.investmentQuestionaireExternalUrl,
      'Failed to get investment questionaire redirect url, check configuration!'
    );
  }

  public goToAmlFormExternalForm(): void {
    this.goToExternalUrl(
      EnvironmentKeys.amlFormExternalUrl,
      'Failed to get AML form redirect url, check configuration!'
    );
  }

  public goToAppStore(device: ApiDevicePlatforms): void {
    if (device === ApiDevicePlatforms.ANDROID) {
      this.goToExternalUrlDirectly(
        EnvironmentKeys.appInAndroidGoogleStoreExternalUrl,
        'Failed to redirect to Google Store, please check external url in config!'
      );
    }else if (device === ApiDevicePlatforms.IOS)
      this.goToExternalUrlDirectly(
        EnvironmentKeys.appInAppleAppStoreExternalUrl,
        'Failed to redirect to App Store, please check external url in config!'
      );
    else {
      this.loggerService.error('Unsupported device type: ' + device);
    }
  }

  private goToExternalUrl(key: EnvironmentKeys, errorMsg: string) {
    const externalUrl = this.configService.getConfig(key);
    if (!externalUrl) {
      this.loggerService.error(errorMsg);
      return;
    }

    const redirectUrl = externalUrl + '?redirect_after_complete_uri=' + this.localAppUrl;

    if(!this.nativeCheckService.isNativePlatform()) {
      this.browserDocument.location.href = redirectUrl;
    } else {
      this.handleNativeBrowser(redirectUrl);
    }
  }

  private goToExternalUrlDirectly(key: EnvironmentKeys, errorMsg: string) {
    const externalUrl = this.configService.getConfig(key);
    if (!externalUrl) {
      this.loggerService.error(errorMsg);
      return;
    }

    Browser.open({ url: externalUrl });
  }

  private handleNativeBrowser(externalUrl: string): void {
    this.browserService.open(externalUrl);

    if(!this.browserService.networkConnected) {
      this.browserService.close();
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.browserQuestionaireLoadError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return;
    }

    this.urlChange$ = this.browserService.onUrlChange().subscribe((navUrl) => {
      if (navUrl.startsWith(this.localAppUrl)) {
        this.zone.run(() => {
          this.router.navigate(['/summary'], { replaceUrl: true });
        });
        this.browserService.close();
        this.unsubBrowser();
      }
    });

    this.browserClose$ = this.browserService.onBrowserClose().subscribe(() => {
      this.unsubBrowser();
    });
  }

  private unsubBrowser(): void {
    this.browserClose$?.unsubscribe();
    this.urlChange$?.unsubscribe();
    this.browserClose$ = null;
    this.urlChange$ = null;
  }
}
